'use client';

import { useCallback, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import {
  mapCredentialToDialogData,
  sanitizeCredential,
  toasterApi,
  useCredentialListController,
  type CredentialDataSources,
  type CredentialListMutations,
} from '@terra/ui-design-system';
import {
  useAbortApplicationCredentialVerification,
  useDeleteApplicationCredential,
  useSaveApplicationCredential,
  useToggleApplicationCredentialStatus,
  useVerifyAllApplicationCredentials,
  useVerifyApplicationCredential,
} from '../../../hooks/api/useApplicationCredentialMutations';
import {
  applyAcceptedCredentialPendingEdits,
  type AcceptedCredentialPendingEdits,
} from '../../object-context/credentialPendingEditsAccept.utils';
import type { BackendCredential } from './applicationCredential.transforms';
import type { useTargetCredentialActions } from './useTargetCredentialActions';
import type { useTargetRoleActions } from './useTargetRoleActions';

const requireApplicationId = (applicationId: string | undefined): string => {
  if (!applicationId) {
    throw new Error('No target selected');
  }

  return applicationId;
};

export function useTargetCredentialList({
  applicationId,
  credentials,
  credentialActions,
  roleActions,
  refetchRoles,
}: {
  applicationId: string | undefined;
  credentials: BackendCredential[];
  credentialActions: ReturnType<typeof useTargetCredentialActions>;
  roleActions: ReturnType<typeof useTargetRoleActions>;
  refetchRoles: () => Promise<unknown>;
}) {
  const t = useTranslations('settings');
  const { mutateAsync: saveCredential } = useSaveApplicationCredential();
  const { mutateAsync: deleteCredential } = useDeleteApplicationCredential();
  const { mutateAsync: toggleCredentialStatus } =
    useToggleApplicationCredentialStatus();
  const { mutateAsync: verifyCredential } = useVerifyApplicationCredential();
  const { mutateAsync: verifyAllCredentials } =
    useVerifyAllApplicationCredentials();
  const { mutateAsync: abortCredentialVerification } =
    useAbortApplicationCredentialVerification();

  const mutations = useMemo<CredentialListMutations>(
    () => ({
      saveCredential: (body) =>
        saveCredential({
          applicationId: requireApplicationId(applicationId),
          body,
        }),
      deleteCredential: (credentialId) =>
        deleteCredential({
          applicationId: requireApplicationId(applicationId),
          credentialId,
        }),
      toggleCredentialStatus: ({ credentialId, isDisabled }) =>
        toggleCredentialStatus({
          applicationId: requireApplicationId(applicationId),
          credentialId,
          isDisabled,
        }),
      verifyCredential: ({ credentialId, streamingId }) =>
        verifyCredential({
          applicationId: requireApplicationId(applicationId),
          credentialId,
          streamingId,
        }),
      verifyAllCredentials: async ({ streamingIds }) => {
        const result = await verifyAllCredentials({
          applicationId: requireApplicationId(applicationId),
          streamingIds,
        });

        return {
          success: result.success,
          error: result.success ? null : result.message,
          results: result.results,
        };
      },
      abortCredentialVerification: (credentialId) =>
        abortCredentialVerification({
          applicationId: requireApplicationId(applicationId),
          credentialId,
        }),
    }),
    [
      abortCredentialVerification,
      applicationId,
      deleteCredential,
      saveCredential,
      toggleCredentialStatus,
      verifyAllCredentials,
      verifyCredential,
    ],
  );

  const { handleCreateRole } = roleActions;
  const dataSources = useMemo<CredentialDataSources>(
    () => ({
      fetchMfaMessages: credentialActions.handleFetchMfaMessages,
      fetchMfaWebhookConfig: credentialActions.handleFetchMfaWebhookConfig,
      fetchCredentialInbox: credentialActions.handleFetchCredentialInbox,
      generateMailbox: credentialActions.handleGenerateMailbox,
      createRole: async (role) => {
        try {
          const created = await handleCreateRole(role);

          await refetchRoles();

          return { id: created.id, name: created.name };
        } catch (error) {
          console.warn(
            'Failed to create role from credential dialog; showing create error',
            error,
          );

          return null;
        }
      },
    }),
    [
      credentialActions.handleFetchCredentialInbox,
      credentialActions.handleFetchMfaMessages,
      credentialActions.handleFetchMfaWebhookConfig,
      credentialActions.handleGenerateMailbox,
      handleCreateRole,
      refetchRoles,
    ],
  );

  const controller = useCredentialListController({ credentials, mutations });

  const persistAcceptedPendingEdits = useCallback(
    async (
      acceptedByCredentialId: Record<string, AcceptedCredentialPendingEdits>,
    ) => {
      const updatedCredentials = applyAcceptedCredentialPendingEdits({
        credentials: credentials.filter(
          (credential) => acceptedByCredentialId[credential.id],
        ),
        acceptedByCredentialId,
      });

      try {
        await Promise.all(
          updatedCredentials.map((credential) =>
            mutations.saveCredential(
              sanitizeCredential(mapCredentialToDialogData(credential)),
            ),
          ),
        );
      } catch (error) {
        console.error('Failed to save accepted credential edits:', error);
        toasterApi.error({
          title: t('saveFailed'),
          description: t('saveFailedDescription'),
        });
      }
    },
    [credentials, mutations, t],
  );

  return { ...controller, dataSources, persistAcceptedPendingEdits };
}
